'use client';

import { useEffect, useState } from 'react';
import { apiGet } from '@/lib/apiClient';
import { getAuthToken } from '@/utils/auth';
import { buildCanHelpers } from '@/utils/permissionPaths';
// Re-exported so existing callers (`import { buildCanHelpers } from
// '@/hooks/usePermissions'`) keep working unchanged after the helper
// moved to `permissionPaths.js` for test-friendliness.
export { buildCanHelpers };

/**
 * Returns the currently signed-in user's effective permissions.
 *
 * Shape:
 *   {
 *     isSchoolAdmin: boolean,            // true → bypass all checks
 *     features: { canApproveLeaves, canAdjustBalance },
 *     sections: { holidays: {read, write, update, delete}, ... },
 *     loading: boolean,
 *     error: string | null,
 *   }
 *
 * Defaults (loading state OR fetch failure) deny everything except
 * for cached `userRole === 'schooladmin'`, which we honour
 * optimistically so the admin doesn't see "Loading…" on every page.
 * The hook still hits `/api/permissions/me` to refresh the cache —
 * if the server later disagrees, the UI updates to match.
 *
 * Hook caching: a module-scoped promise dedupes concurrent calls
 * across the page tree, so e.g. AdminLeavesQueue + LeavesPage share
 * a single HTTP request even when both mount in the same render.
 */
let _cache = null; // { fetchedAt, data }
let _inflight = null;
const FRESH_FOR_MS = 60 * 1000;

const optimisticFromRole = () => {
  if (typeof window === 'undefined') {
    return { isSchoolAdmin: false, features: {}, sections: {} };
  }
  let role = '';
  try {
    role = (sessionStorage.getItem('userRole') || '').toLowerCase();
  } catch {
    role = '';
  }
  if (role === 'schooladmin') {
    return {
      isSchoolAdmin: true,
      features: { canApproveLeaves: true, canAdjustBalance: true },
      sections: {},
    };
  }
  return { isSchoolAdmin: false, features: {}, sections: {} };
};

const fetchPermissions = async () => {
  // Cache is per signed-in user: after a sign-out + sign-in as someone
  // else in the same tab, the previous account's permissions must not be
  // served for the remaining seconds of the window.
  const token = getAuthToken() || '';
  if (_cache && _cache.token === token && Date.now() - _cache.fetchedAt < FRESH_FOR_MS) return _cache.data;
  if (_inflight && _inflight.token === token) return _inflight.promise;
  const promise = (async () => {
    try {
      const res = await apiGet('/api/permissions/me');
      const data = {
        isSchoolAdmin: !!res?.isSchoolAdmin,
        features: res?.features || {},
        sections: res?.sections || {},
      };
      _cache = { fetchedAt: Date.now(), token, data };
      return data;
    } finally {
      if (_inflight?.token === token) _inflight = null;
    }
  })();
  _inflight = { token, promise };
  return promise;
};

/** Drop the cache — call after sign-out / account switch. */
export const invalidatePermissions = () => {
  _cache = null;
  _inflight = null;
};

export function usePermissions() {
  const [state, setState] = useState(() => ({
    ...optimisticFromRole(),
    loading: true,
    error: null,
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPermissions();
        if (!cancelled) setState({ ...data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err?.message || 'Permission lookup failed',
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Convenience read used by gate-and-go components that don't want a hook. */
export const hasFeatureSync = (flag) => {
  if (_cache?.data?.isSchoolAdmin) return true;
  return !!_cache?.data?.features?.[flag];
};

/**
 * True when the signed-in user may bypass time-lock restrictions
 * (edit expired items, save marks past the evaluation deadline,
 * back-fill attendance). Schooladmin always may; others need the
 * `canBypassRestrictions` feature from the Permission Matrix. The
 * backend re-checks on every call and stamps actual use into
 * Activity Logs — this hook only unlocks the UI affordances.
 */
export function useCanBypass() {
  const perms = usePermissions();
  return perms.isSchoolAdmin || !!perms.features?.canBypassRestrictions;
}
